import React, { useState, useEffect } from 'react';
import { Label, labelApi } from '../services/api';
import { Plus, Xmark, EditPencil, Check } from 'iconoir-react';

interface LabelManagerProps {
  cardId: number;
  boardId?: number;
  onLabelsChange?: () => void;
}

const PRESET_COLORS = [
  '#61BD4F', // Vert
  '#F2D600', // Jaune
  '#FF9F1A', // Orange
  '#EB5A46', // Rouge
  '#C377E0', // Violet
  '#0079BF', // Bleu
  '#00C2E0', // Cyan
  '#51E898', // Vert clair
  '#FF78CB', // Rose
  '#C4C9CC', // Gris
];

export const LabelManager: React.FC<LabelManagerProps> = ({ cardId, boardId, onLabelsChange }) => {
  const [labels, setLabels] = useState<Label[]>([]);
  const [boardLabels, setBoardLabels] = useState<Array<{ name: string; color: string }>>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(PRESET_COLORS[0]);
  const [editingLabelId, setEditingLabelId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadLabels();
    if (boardId) {
      loadBoardLabels();
    }
  }, [cardId, boardId]);

  const loadLabels = async () => {
    try {
      const response = await labelApi.getAll(cardId);
      setLabels(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des étiquettes', error);
    }
  };

  const loadBoardLabels = async () => {
    if (!boardId) return;
    try {
      const response = await labelApi.getBoardLabels(boardId);
      setBoardLabels(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des étiquettes du board', error);
    }
  };

  const handleAddLabel = async () => {
    if (!newLabelName.trim()) return;

    setIsLoading(true);
    try {
      await labelApi.create(cardId, {
        name: newLabelName.trim(),
        color: newLabelColor,
      });
      
      setNewLabelName('');
      setNewLabelColor(PRESET_COLORS[0]);
      setIsAdding(false);
      await loadLabels();
      if (boardId) {
        await loadBoardLabels();
      }
      onLabelsChange?.();
    } catch (error) {
      console.error('Erreur lors de la création de l\'étiquette', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectExistingLabel = async (labelName: string, labelColor: string) => {
    setIsLoading(true);
    try {
      await labelApi.create(cardId, {
        name: labelName,
        color: labelColor,
      });
      
      await loadLabels();
      onLabelsChange?.();
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'étiquette', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateLabel = async (labelId: number) => {
    if (!editName.trim()) return;

    setIsLoading(true);
    try {
      await labelApi.update(cardId, labelId, {
        name: editName.trim(),
        color: editColor,
      });
      
      setEditingLabelId(null);
      await loadLabels();
      onLabelsChange?.();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'étiquette', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLabel = async (labelId: number) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette étiquette ?')) return;

    setIsLoading(true);
    try {
      await labelApi.delete(cardId, labelId);
      await loadLabels();
      onLabelsChange?.();
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'étiquette', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (label: Label) => {
    setEditingLabelId(label.id);
    setEditName(label.name);
    setEditColor(label.color);
  };

  const cancelEdit = () => {
    setEditingLabelId(null);
    setEditName('');
    setEditColor('');
  };

  return (
    <div className="label-manager">
      <div className="label-manager-header">
        <h4>Étiquettes</h4>
        <button
          className="btn-icon"
          onClick={() => setIsAdding(!isAdding)}
          title="Ajouter une étiquette"
        >
          <Plus width={18} height={18} strokeWidth={2} />
        </button>
      </div>

      {/* Liste des étiquettes */}
      <div className="labels-list">
        {labels.map(label => (
          <div key={label.id} className="label-item">
            {editingLabelId === label.id ? (
              <div className="label-edit-form">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nom de l'étiquette"
                  className="label-input"
                  disabled={isLoading}
                />
                <div className="color-picker">
                  {PRESET_COLORS.map(color => (
                    <button
                      key={color}
                      className={`color-option ${editColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setEditColor(color)}
                      disabled={isLoading}
                    />
                  ))}
                </div>
                <div className="label-actions">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleUpdateLabel(label.id)}
                    disabled={isLoading || !editName.trim()}
                  >
                    <Check width={16} height={16} strokeWidth={2} />
                    Enregistrer
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={cancelEdit}
                    disabled={isLoading}
                  >
                    <Xmark width={16} height={16} strokeWidth={2} />
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <div className="label-display">
                <div className="label-badge" style={{ backgroundColor: label.color }}>
                  {label.name}
                </div>
                <div className="label-buttons">
                  <button
                    className="btn-icon-small"
                    onClick={() => startEdit(label)}
                    title="Modifier"
                  >
                    <EditPencil width={14} height={14} strokeWidth={2} />
                  </button>
                  <button
                    className="btn-icon-small"
                    onClick={() => handleDeleteLabel(label.id)}
                    title="Supprimer"
                  >
                    <Xmark width={14} height={14} strokeWidth={2} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Formulaire d'ajout et sélection d'étiquettes existantes */}
      {isAdding && (
        <div className="label-add-section">
          {/* Formulaire de création */}
          <div className="label-add-form">
            <p className="form-section-title">Créer une nouvelle étiquette :</p>
            <input
              type="text"
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
              placeholder="Nom de l'étiquette"
              className="label-input"
              disabled={isLoading}
              onKeyDown={(e) => e.key === 'Enter' && handleAddLabel()}
            />
            <div className="color-picker">
              {PRESET_COLORS.map(color => (
                <button
                  key={color}
                  className={`color-option ${newLabelColor === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setNewLabelColor(color)}
                  disabled={isLoading}
                />
              ))}
            </div>
            <div className="label-actions">
              <button
                className="btn btn-sm btn-primary"
                onClick={handleAddLabel}
                disabled={isLoading || !newLabelName.trim()}
              >
                <Plus width={16} height={16} strokeWidth={2} />
                Créer
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setIsAdding(false);
                  setNewLabelName('');
                  setNewLabelColor(PRESET_COLORS[0]);
                }}
                disabled={isLoading}
              >
                <Xmark width={16} height={16} strokeWidth={2} />
                Annuler
              </button>
            </div>
          </div>

          {/* Sélection d'étiquettes existantes */}
          {boardId && boardLabels.length > 0 && (
            <div className="existing-labels-list">
              <p className="existing-labels-title">Étiquettes existantes du tableau :</p>
              {boardLabels
                .filter(boardLabel => !labels.some(l => l.name === boardLabel.name && l.color === boardLabel.color))
                .map((boardLabel, index) => (
                  <div
                    key={`${boardLabel.name}-${index}`}
                    className="existing-label-item"
                    onClick={() => handleSelectExistingLabel(boardLabel.name, boardLabel.color)}
                  >
                    <div className="label-badge" style={{ backgroundColor: boardLabel.color }}>
                      {boardLabel.name}
                    </div>
                  </div>
                ))}
              {boardLabels.filter(boardLabel => !labels.some(l => l.name === boardLabel.name && l.color === boardLabel.color)).length === 0 && (
                <p className="no-labels-message">Toutes les étiquettes du tableau sont déjà utilisées sur cette carte.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
